(function () {
  'use strict';
  
  const RECONCILE_DEBOUNCE_MS = 300;
  const EMPTY_CLASS_OBSERVER_MS = 5000;
  
  // Check if Loop coverage product is in the cart
  function isLoopCoverageItem(item) {
    if (!item) return false;
 
    const label = ((item.product_title || '') + ' ' + (item.handle || '')).toLowerCase();
    return /return[\s_-]*protection|free[\s_-]*returns/.test(label);
  }
 
  // Fetch wrapper that returns parsed JSON or null
  function fetchJSON(url, opts) {
    return fetch(url, Object.assign({ credentials: 'same-origin' }, opts || {}))
      .then(function (response) {
        if (!response.ok) return null;
        const contentType = (response.headers.get('content-type') || '').toLowerCase();
        if (contentType.includes('html')) return null;
        return response.text().then(function (txt) {
          try { return JSON.parse(txt); } catch (_) { return null; }
        });
      })
      .catch(function () { return null; });
  }

  let emptyClassObserver = null;
  let emptyClassTimeout = null;

  // Add is-empty to <cart-drawer>, then watch its class and re-add it
  // if anything strips it. Auto-disconnects after EMPTY_CLASS_OBSERVER_MS
  function enforceEmptyClass() {
    releaseEmptyClass();
 
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
 
    drawer.classList.add('is-empty');

    if (typeof MutationObserver !== 'function') return; 

    emptyClassObserver = new MutationObserver(function () {
      if (!drawer.classList.contains('is-empty')) {
        drawer.classList.add('is-empty');
      }
    });

    emptyClassObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ['class']
    });
 
    emptyClassTimeout = setTimeout(releaseEmptyClass, EMPTY_CLASS_OBSERVER_MS);
  }

  // Disconnect the MutationObserver and clear the auto-expire timeout
  function releaseEmptyClass() {
    if (emptyClassObserver) { emptyClassObserver.disconnect(); emptyClassObserver = null; }
    if (emptyClassTimeout) { clearTimeout(emptyClassTimeout); emptyClassTimeout = null; }
  }
 
  // Force the cart and drawer into their empty state: hide the items form, refresh the cart sections
  function forceEmptyCartUI() {

    // Hide the live form
    document.querySelectorAll('cart-items .cart__contents').forEach(function (form) {
      form.classList.add('critical-hidden');
      form.setAttribute('aria-hidden', 'true');
    });
    document.querySelectorAll('cart-items .cart__warnings').forEach(function (warning) {
      warning.style.removeProperty('display');
    });
 
    // Refresh the dedicated /cart page sections
    const sectionIds = [];
    document.querySelectorAll('[id^="shopify-section-"]').forEach(function (el) {
      if (/cart-items|cart-footer/.test(el.id)) {
        sectionIds.push(el.id.replace('shopify-section-', ''));
      }
    });
 
    if (sectionIds.length) {
      fetchJSON('/?sections=' + sectionIds.join(',')).then(function (sections) {
        if (!sections) return;
        sectionIds.forEach(function (id) {
          const html = sections[id];
          if (!html) return;
 
          const container = document.getElementById('shopify-section-' + id);
          if (!container) return;
 
          const wrapper = document.createElement('div');
          wrapper.innerHTML = html;
 
          const freshSection = wrapper.firstElementChild;
          if (freshSection) container.innerHTML = freshSection.innerHTML;
        });
      });
    }
 
    // Refresh the side cart drawer's interior
    const currentInner = document.querySelector('cart-drawer .drawer__inner');
    if (currentInner) {
      fetchJSON('/?sections=cart-drawer,cart-icon-bubble').then(function (sections) {
        if (!sections) return;
        if (sections['cart-drawer']) {
          const wrapper = document.createElement('div');
          wrapper.innerHTML = sections['cart-drawer'];
          const freshInner = wrapper.querySelector('.drawer__inner');
          if (freshInner && currentInner) currentInner.innerHTML = freshInner.innerHTML;
          enforceEmptyClass();
        }
 
        if (sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble') ||
                         document.querySelector('#shopify-section-cart-icon-bubble');
          if (bubble) bubble.innerHTML = sections['cart-icon-bubble'];
        }
      });
    }
  }
 
  function removeLoopItem(line) {
    return fetchJSON('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: 0 })
    });
  }
 
  let isRunning = false;
  let reconcileTimer = null;
 
  // True when cart-drawer-items has been clobbered with the dedicated /cart
  // page's #main-cart-items markup — needs the heavy section refetch
  function drawerHasContamination() {
    const cdi = document.querySelector('cart-drawer cart-drawer-items');
    return cdi ? !!cdi.querySelector('#main-cart-items') : false;
  }
 
  // Returns true if cartData indicates the cart will be empty (either 0
  // items or only Loop coverage items remaining)
  function cartWillBeEmpty(items) {
    if (!Array.isArray(items)) return false;
    if (items.length === 0) return true;
    return items.every(isLoopCoverageItem);
  }
 
  // Runs after every cart-update event, strip leftover Loop coverage items
  // and repaint the empty state when the cart is logically empty
  async function reconcile() {
    if (isRunning) return;
    isRunning = true;
    try {
      const cart = await fetchJSON('/cart.js');
      if (!cart || !Array.isArray(cart.items)) return;
 
      const logicallyEmpty = cart.items.length === 0 || cart.items.every(isLoopCoverageItem);

      if (logicallyEmpty) {
        if (cart.items.length === 0 && drawerHasContamination()) {
          forceEmptyCartUI();
        }

        enforceEmptyClass();

        if (cart.items.length > 0) {
          for (let i = cart.items.length; i >= 1; i--) {
            await removeLoopItem(i);
          }
          forceEmptyCartUI();
        }
      } else {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.classList.remove('is-empty');
        releaseEmptyClass();
      }
    } finally {
      isRunning = false;
    }
  }
 
  // Returns the theme's PUB_SUB_EVENTS object
  function getPubSubEvents() {
    if (typeof PUB_SUB_EVENTS !== 'undefined') return PUB_SUB_EVENTS;
    return null;
  }
 
  // Subscribe reconcile() to cart-update.
  // Returns false if theme JS isn't ready
  function wire() {
    const pse = getPubSubEvents();
    if (typeof window.subscribe !== 'function' || !pse) {
      return false;
    }
 
    window.subscribe(pse.cartUpdate, function (data) {
      const source = data?.source;
      const items = data?.cartData?.items;

      if (items) {
        if (!cartWillBeEmpty(items)) {
          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.classList.remove('is-empty');
          releaseEmptyClass();
        } else {
          enforceEmptyClass();
        }
      }

      if (source === 'cart-items') {
        clearTimeout(reconcileTimer);
        reconcileTimer = null;
        Promise.resolve().then(reconcile);
      } else if (reconcileTimer === null) {
        reconcileTimer = setTimeout(function () {
          reconcileTimer = null;
          reconcile();
        }, RECONCILE_DEBOUNCE_MS);
      }
    });
 
    return true;
  }
 
  if (!wire()) {
    let attempts = 0;
    const interval = setInterval(function () {
      if (wire() || ++attempts > 40) clearInterval(interval);
    }, 250);
  }
})();